import React, { useState, useEffect } from 'react';
import { DailyReportCard, EnergyCUFChart, EnvironmentalImpactCard, ReportExportPanel } from '../components/DailyReports';
import { dailyReportService, DailyReport, EnvironmentalImpact } from '../services/dailyReportService';

export const DailyReports: React.FC = () => {
  const [reports, setReports] = useState<DailyReport[]>([]);
  const [environmentalImpact, setEnvironmentalImpact] = useState<EnvironmentalImpact | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [selectedPlant, setSelectedPlant] = useState<number | undefined>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [selectedDate, selectedPlant]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reportsData, impactData] = await Promise.all([
        dailyReportService.getDailyReports(selectedPlant, selectedDate),
        dailyReportService.getEnvironmentalImpact(selectedDate, selectedPlant)
      ]);
      setReports(reportsData);
      setEnvironmentalImpact(impactData);
    } catch (error) {
      console.error('Error fetching daily reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = () => {
    console.log('Exporting PDF...');
  };

  const handleExportCSV = () => {
    if (reports.length === 0) return;
    
    const csvContent = [
      ['Date', 'Plant', 'Energy (kWh)', 'Specific Yield', 'CUF (%)', 'PR (%)', 'CO2 Saved (Tons)'],
      ...reports.map(report => [
        report.date,
        report.plant_name,
        report.total_energy_kwh.toFixed(2),
        report.specific_yield.toFixed(2),
        report.cuf.toFixed(2),
        report.pr.toFixed(2),
        report.co2_saved_tons.toFixed(2)
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `daily-report-${selectedDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Daily Performance Reports</h1>
        <div className="flex gap-4">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {reports.length > 0 && (
        <>
          <DailyReportCard report={reports[0]} />
          
          {environmentalImpact && (
            <EnvironmentalImpactCard impact={environmentalImpact} />
          )}
          
          <EnergyCUFChart data={reports} />
          
          <ReportExportPanel 
            onExportPDF={handleExportPDF}
            onExportCSV={handleExportCSV}
          />
        </>
      )}

      {reports.length === 0 && !loading && (
        <div className="text-center py-12">
          <p className="text-gray-500">No reports available for the selected date.</p>
        </div>
      )}
    </div>
  );
};